import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import type { Control } from "react-hook-form";
import type { AdverseEventType } from "../../../../api/client/types/catalog.types";
import type { AdverseEventNewFormData } from "../RegisterNewForm/RegisterNewForm.types";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import { ADVERSE_EVENT_TYPE_NAMES } from "../../utils/reportTypeConstants";

interface UseAdverseEventNewFormFieldsProps {
  control: Control<AdverseEventNewFormData>;
  adverseEventTypes: AdverseEventType[];
  swimmer?: Swimmer;
}

/**
 * Custom hook for AdverseEventNewFormFields business logic.
 *
 * Handles:
 * - Watching the selected adverse event type
 * - Determining which options should be disabled based on swimmer presence
 * - Checking if "Condiciones Meteorológicas" is selected
 * - Computing disabled options state
 *
 * Business rules:
 * - Only "Condiciones Meteorológicas" is available when there's no swimmer.
 * - "Condiciones Meteorológicas" is NOT available when a swimmer exists (must be created without swimmer).
 * - All other adverse event types require a swimmer to be associated.
 */
export const useAdverseEventNewFormFields = ({
  control,
  adverseEventTypes,
  swimmer,
}: UseAdverseEventNewFormFieldsProps) => {
  // Watch the selected adverse event type ID
  const selectedAdverseEventTypeId = useWatch({
    control,
    name: "adverse_events_type_id",
  });

  // Check if "Condiciones Meteorológicas" is selected
  const isMeteorologicalConditionsSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return (
      selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.METEOROLOGICAL_CONDITIONS
    );
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  // Check if "Accidente" is selected
  const isAccidentSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.ACCIDENT;
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  // Check if "Incidentes" is selected
  const isIncidentSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.INCIDENTS;
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  const isAccidentOrIncidentSelected = isAccidentSelected || isIncidentSelected;

  // Check if any adverse event type other than "Condiciones Meteorológicas" is selected
  // This is used to show the informational note for all non-meteorological adverse event types
  const isNonMeteorologicalAdverseEventSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return (
      selectedType?.name !== ADVERSE_EVENT_TYPE_NAMES.METEOROLOGICAL_CONDITIONS
    );
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  // Create options with disabled states based on swimmer presence
  const adverseEventTypeOptions = useMemo(() => {
    return adverseEventTypes.map((adverseEventType) => {
      const isMeteorologicalConditions =
        adverseEventType.name ===
        ADVERSE_EVENT_TYPE_NAMES.METEOROLOGICAL_CONDITIONS;

      // Disable logic:
      // - If no swimmer: disable all EXCEPT meteorological conditions
      // - If swimmer exists: disable meteorological conditions (doesn't need swimmer)
      const isDisabledNoSwimmer = !swimmer && !isMeteorologicalConditions;
      const isDisabledWithSwimmer = swimmer && isMeteorologicalConditions;
      const isDisabled = isDisabledNoSwimmer || isDisabledWithSwimmer;

      return {
        label: adverseEventType.name,
        value: adverseEventType.id.toString(),
        disabled: isDisabled,
        // Track why it's disabled:
        // - needsSwimmer: true = requires swimmer (disabled when no swimmer)
        // - doesntNeedSwimmer: true = doesn't need swimmer (disabled when swimmer exists)
        needsSwimmer: isDisabledNoSwimmer,
        doesntNeedSwimmer: isDisabledWithSwimmer,
      };
    });
  }, [adverseEventTypes, swimmer]);

  // Check if there are any disabled options
  const hasDisabledOptions = useMemo(() => {
    return adverseEventTypeOptions.some((opt) => opt.disabled);
  }, [adverseEventTypeOptions]);

  return {
    isMeteorologicalConditionsSelected,
    isAccidentSelected,
    isIncidentSelected,
    isAccidentOrIncidentSelected,
    isNonMeteorologicalAdverseEventSelected,
    adverseEventTypeOptions,
    hasDisabledOptions,
  };
};
